import { useCallback, useEffect, useMemo, useState } from 'react';

import { ICardPurseDtoStatusEnum } from '@/dtos';

import { CardPurseAccess, CardPursePatchPayload } from '../ManageAccountAccessModal.types';

interface Params {
  purses: CardPurseAccess[];
}

const getFrozenPurseIds = (purses: CardPurseAccess[]) =>
  purses.filter((purse) => purse.status === ICardPurseDtoStatusEnum.FROZEN).map((purse) => purse.id);

export const useManageAccountAccessState = ({ purses }: Params) => {
  const [lockedIds, setLockedIds] = useState<string[]>(() => getFrozenPurseIds(purses));

  useEffect(() => {
    setLockedIds(getFrozenPurseIds(purses));
  }, [purses]);

  const toggleLock = useCallback((id: string, locked: boolean) => {
    setLockedIds((prev) => (locked ? [...prev, id] : prev.filter((purseId) => purseId !== id)));
  }, []);

  const changedPurses = useMemo(
    () => purses.filter((purse) => lockedIds.includes(purse.id) !== (purse.status === ICardPurseDtoStatusEnum.FROZEN)),
    [purses, lockedIds]
  );

  const hasChanges = useMemo(() => changedPurses.length > 0, [changedPurses]);

  const reset = useCallback(() => {
    setLockedIds(getFrozenPurseIds(purses));
  }, [purses]);

  const buildPayload = useCallback(
    (): CardPursePatchPayload[] =>
      changedPurses.map((purse) => ({
        id: Number(purse.id),
        status: lockedIds.includes(purse.id) ? ICardPurseDtoStatusEnum.FROZEN : ICardPurseDtoStatusEnum.ACTIVE,
      })),
    [changedPurses, lockedIds]
  );

  return {
    lockedIds,
    hasChanges,
    toggleLock,
    reset,
    buildPayload,
  };
};
